import React, { useState } from 'react';
import { Box, Button, Popover, Typography } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

export interface GuidanceButtonProps {
  /** Texto do botão. Padrão: "Orientações". */
  label?: string;
  /** Título opcional dentro do popover. */
  title?: string;
  /** Conteúdo das orientações. */
  children: React.ReactNode;
  size?: 'small' | 'medium';
  /** Estilos extras do papel do popover (ex.: largura maior para guias longos). */
  paperSx?: Record<string, unknown>;
}

/**
 * Botão "Orientações" com cor vibrante que abre um popover com as instruções.
 * Substitui blocos de texto explicativos inline para reduzir poluição visual
 * (sugestão médica — PDF 29.05).
 */
const GuidanceButton: React.FC<GuidanceButtonProps> = ({
  label = 'Orientações',
  title,
  children,
  size = 'small',
  paperSx,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        size={size}
        startIcon={<LightbulbOutlinedIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          color: '#fff',
          background: 'linear-gradient(90deg,#f59e0b,#f97316)',
          borderRadius: 999,
          px: 1.5,
          boxShadow: '0 2px 6px rgba(249,115,22,0.35)',
          '&:hover': { background: 'linear-gradient(90deg,#d97706,#ea580c)' },
        }}
      >
        {label}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 2,
            maxWidth: 440,
            borderRadius: 2,
            borderTop: '4px solid #f59e0b',
            ...paperSx,
          },
        }}
      >
        {title && (
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75, color: '#b45309' }}>
            {title}
          </Typography>
        )}
        <Box sx={{ fontSize: '0.8125rem', color: '#475569', lineHeight: 1.65 }}>{children}</Box>
      </Popover>
    </>
  );
};

export default GuidanceButton;
